<template>
  <v-app>
    <v-main>
      <v-container fluid fill-height>
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="6" lg="4">
            <v-card elevation="12">
              <v-card-title class="text-h4 justify-center pa-6">
                Living Knowledge
              </v-card-title>

              <v-card-subtitle class="text-center pb-4">
                Reset Your Password
              </v-card-subtitle>

              <v-card-text>
                <v-alert
                  v-if="error"
                  type="error"
                  dismissible
                  @input="error = null"
                >
                  {{ error }}
                </v-alert>

                <v-alert
                  v-if="successMessage"
                  type="success"
                  class="mb-4"
                >
                  {{ successMessage }}
                </v-alert>

                <v-alert v-if="ssoMessage" type="info" class="mb-4">
                  {{ ssoMessage }}
                </v-alert>

                <template v-if="!successMessage && !ssoMessage">
                  <p class="text-body-1 mb-4">
                    Enter your email address and we'll send you a link
                    to reset your password.
                  </p>

                  <v-text-field
                    v-model="email"
                    label="Email Address"
                    :rules="emailRules"
                    type="email"
                    required
                    outlined
                    :disabled="loading"
                    @keydown.enter="onSubmit"
                  />

                  <v-alert type="info" dense text class="mb-4">
                    Note: This is only for non-Deakin users. Deakin
                    users should use Deakin SSO to sign in.
                  </v-alert>

                  <v-btn
                    color="primary"
                    block
                    large
                    :disabled="!isValid"
                    :loading="loading"
                    @click="onSubmit"
                  >
                    Send Reset Link
                  </v-btn>
                </template>

                <v-btn
                  v-if="successMessage || ssoMessage"
                  color="primary"
                  block
                  large
                  @click="goToLogin"
                >
                  Go to login
                </v-btn>
              </v-card-text>

              <v-divider />

              <v-card-actions class="justify-center pa-4">
                <v-btn text @click="goToLogin"> Back to Login </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { EMAIL_REGEX } from '@/modules';
import {
  isDeakinEmail,
  requestPasswordReset,
} from '@/auth/passwordReset';

export default {
  name: 'PasswordResetPage',
  data: () => ({
    email: '',
    loading: false,
    error: null,
    successMessage: null,
    ssoMessage: null,
    emailRules: [
      (v) => !!v || 'Email is required',
      (v) => EMAIL_REGEX.test(v) || 'Email must be valid',
    ],
  }),
  computed: {
    isValid() {
      return this.email && EMAIL_REGEX.test(this.email);
    },
  },
  methods: {
    async onSubmit() {
      if (!this.isValid) return;

      // Client-side Deakin SSO hint. We never send Deakin addresses to the
      // server because (a) Deakin accounts authenticate via SSO and have no
      // password to reset, and (b) the server intentionally treats Deakin
      // and non-Deakin emails identically — so this is the only place the
      // user can be given a useful hint without leaking from the server.
      if (isDeakinEmail(this.email)) {
        this.error = null;
        this.successMessage = null;
        this.ssoMessage =
          'Deakin accounts use Single Sign-On — please sign in via the main login page.';
        return;
      }

      try {
        this.loading = true;
        this.error = null;
        this.successMessage = null;
        this.ssoMessage = null;

        await requestPasswordReset(this.email);

        // The server always returns the same generic 200 success body
        // regardless of whether the email exists, so we display the
        // matching generic message here.
        this.successMessage =
          'If an account exists with that email, a password reset link has been sent. Check your inbox.';
      } catch (err) {
        // We deliberately do NOT inspect err.response.data.message —
        // the server is designed to return a uniform response, and
        // peeking at server messages would re-introduce enumeration
        // signal on the client. The only errors we can see here are
        // transport-level (network down) or 429 (rate limited).
        if (err.response && err.response.status === 429) {
          this.error =
            'Too many requests. Please wait a few minutes and try again.';
        } else {
          this.error =
            'Failed to send password reset email. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },
    goToLogin() {
      // Redirect to the main page, which will trigger Auth0 login
      window.location.href = window.location.origin;
    },
  },
};
</script>

<style scoped>
.v-main {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}
</style>
